import constate from 'constate';
import { useLayoutEffect, useState, type FC, type ReactNode } from 'react';
import Cookies, { type Cookie, type CookieSetOptions } from 'universal-cookie';

const globalCookies = new Cookies();

const setGlobalCookie: (name: string, value: Cookie, options?: CookieSetOptions) => void =
  globalCookies.set.bind(globalCookies);
const removeGlobalCookie: (name: string, options?: CookieSetOptions) => void =
  globalCookies.remove.bind(globalCookies);

function useCookies(): {
  setCookie: typeof setGlobalCookie;
  removeCookie: typeof removeGlobalCookie;
  cookies: Record<string, string>;
} {
  const [cookies, setCookies] = useState(globalCookies.getAll());

  // IMPORTANT: `useLayoutEffect` executes before `useEffect`, which increases the chances
  // that cookie updates are reflected in local state.
  // See https://getcolor.atlassian.net/browse/UITASK-144
  /*
   * Reviewed by UX Programs (Jaz) as a valid `useEffect` usage.
   * See https://getcolor.atlassian.net/browse/UXTASK-1406
   */
  // nosemgrep:semgrep-rules.ui-useEffect
  useLayoutEffect(() => {
    function onChange(): void {
      const newCookies = globalCookies.getAll();
      setCookies(newCookies);
    }
    globalCookies.addChangeListener(onChange);
    return (): void => {
      globalCookies.removeChangeListener(onChange);
    };
  }, []);

  return {
    setCookie: setGlobalCookie,
    removeCookie: removeGlobalCookie,
    cookies,
  };
}

const dest = constate(useCookies);
export const CookiesProvider: FC<{
  children?: ReactNode;
}> = dest[0];
export const useCookiesContext: () => {
  setCookie: typeof setGlobalCookie;
  removeCookie: typeof removeGlobalCookie;
  cookies: Record<string, string>;
} = dest[1];
